import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SalesStackedSchedule } from '@dexco/sales-schedule-chart';
import type { DayColumn as LibraryDayColumn, DeliveryCard as LibraryDeliveryCard } from '@dexco/sales-schedule-chart';
import { DayColumn } from './types/wizard';
import { getChartByUuid } from './utils/vendorApi';
import { parseCsvToDayColumns } from './utils/parseCsv';
import { convertWizardToLibraryFormat, getMonthNameFromSchedule } from './utils/scheduleConverter';
import { SecondaryButton, PrimaryButton } from './components/buttons';
import { ChevronLeft, Download, Share2, Calendar, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { UnprioritizedOrdersModal } from './components/UnprioritizedOrdersModal';
import { AuthContext } from './App';
import { getAuthHeader, UserInfo } from './utils/authApi';

interface SchedulePageProps {
    showBackButton?: boolean;
    returnTo?: string;
    onBack?: () => void;
    onLogout?: () => void;
    userInfo?: UserInfo | null;
}

/**
 * Dedicated Schedule Visualization Page
 * Opens in new tab for full-screen chart experience
 */
export const SchedulePage: React.FC<SchedulePageProps> = ({
    showBackButton = false,
    returnTo,
    onBack,
    onLogout: _onLogout,
    userInfo: propUserInfo
}) => {
    const { taskId: urlTaskId } = useParams<{ taskId: string }>();
    const navigate = useNavigate();
    const { userProfile, userInfo: contextUserInfo } = useContext(AuthContext);

    // Use userInfo from props (ChartViewer) or context (direct access)
    const currentUserInfo = propUserInfo || contextUserInfo;

    const [scheduleData, setScheduleData] = useState<DayColumn[] | null>(null);
    const [currentScheduleState, setCurrentScheduleState] = useState<LibraryDayColumn[] | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isUnprioritizedModalOpen, setIsUnprioritizedModalOpen] = useState(false);
    const [isLoadingUnprioritizedModal, setIsLoadingUnprioritizedModal] = useState(false);
    const [isUploadingCsv, setIsUploadingCsv] = useState(false);

    const [taskId, setTaskId] = useState<string | null>(null);

    // Determine if this is a new unified schedule route
    const isUnifiedRoute = !!urlTaskId;
    // Show back button for unified routes or when explicitly requested
    // Simplified logic: show for all users in unified route
    const shouldShowBackButton = showBackButton || isUnifiedRoute;

    // Debug logging for back button
    React.useEffect(() => {
        console.log('🔙 [BACK BUTTON] Debug:', {
            showBackButton,
            isUnifiedRoute,
            userProfile,
            shouldShowBackButton,
            urlTaskId,
            userProfileType: typeof userProfile,
            contextUserInfo: contextUserInfo?.profile_type,
            currentUserInfo: currentUserInfo?.profile_type
        });
    }, [showBackButton, isUnifiedRoute, userProfile, shouldShowBackButton, urlTaskId, contextUserInfo, currentUserInfo]);

    const [convertedData, setConvertedData] = useState<{ schedule: LibraryDayColumn[]; allOrders: LibraryDeliveryCard[] } | null>(null);
    // Active filters state for the chart component (not for unprioritized orders modal)
    const [activeFilters, setActiveFilters] = useState<{ vendedor: string; cliente: string; espessura: string; largura: string }>({
        vendedor: '',
        cliente: '',
        espessura: '',
        largura: '',
    });

    // Keep activeFilters in scope for debugging/logging if needed
    React.useEffect(() => {
        // This prevents the "unused variable" warning while keeping the state for the chart filters
        if (process.env.NODE_ENV === 'development') {
            console.debug('Chart filters updated:', activeFilters);
        }
    }, [activeFilters]);

    // Calculate unprioritized orders count (all orders without production dates)
    // Apply vendor filter if active
    const unprioritizedCount = useMemo(() => {
        if (!convertedData) return 0;
        return convertedData.allOrders.filter(order => {
            const hasNoDate = !order.dt_producao ||
                order.dt_producao === '' ||
                order.dt_producao === 'null' ||
                order.dt_producao === null ||
                order.dt_producao === undefined;

            if (!hasNoDate) return false;

            // Apply vendor filter if active (same logic as in UnprioritizedOrdersModal)
            if (activeFilters.vendedor) {
                const orderVendedor = order.no_vendedor?.toLowerCase() || '';
                const filterVendedor = activeFilters.vendedor.toLowerCase();

                const matches = orderVendedor === filterVendedor ||
                    orderVendedor.includes(filterVendedor) ||
                    filterVendedor.includes(orderVendedor);

                return matches;
            }

            // For limited users (vendedores), only count their own orders
            if (currentUserInfo?.profile_type === 'vendedor') {
                // Normalize function to handle special characters and spaces
                const normalize = (str: string) => str
                    .toLowerCase()
                    .trim()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '') // Remove accents
                    .replace(/\s+/g, ' '); // Normalize spaces

                const normalizedUserName = normalize(currentUserInfo.full_name);
                const normalizedOrderVendedor = normalize(order.no_vendedor || '');

                // Try exact match first
                let canModify = normalizedUserName === normalizedOrderVendedor;

                // If no exact match, try partial match
                if (!canModify) {
                    canModify = normalizedUserName.includes(normalizedOrderVendedor) ||
                        normalizedOrderVendedor.includes(normalizedUserName);
                }

                // If still no match, try word-by-word matching
                if (!canModify && normalizedUserName.length > 0 && normalizedOrderVendedor.length > 0) {
                    const userWords = normalizedUserName.split(' ').filter(word => word.length > 2);
                    const vendedorWords = normalizedOrderVendedor.split(' ').filter(word => word.length > 2);
                    canModify = userWords.some(userWord =>
                        vendedorWords.some(vendedorWord =>
                            userWord === vendedorWord ||
                            userWord.includes(vendedorWord) ||
                            vendedorWord.includes(userWord)
                        )
                    );
                }

                return canModify;
            }

            return true;
        }).length;
    }, [convertedData, activeFilters.vendedor, currentUserInfo]);

    // Load schedule data from sessionStorage or URL params
    useEffect(() => {
        const loadScheduleData = async () => {
            try {
                const storedTaskId = sessionStorage.getItem('dexco-task-id');

                // Debug info
                console.log('🔍 SchedulePage Debug:', {
                    urlTaskId,
                    storedTaskId,
                    currentLocation: window.location.pathname,
                    isUnifiedRoute
                });

                // Priority order: URL taskId > sessionStorage taskId > null (for view-only mode)
                let currentTaskId: string | null = null;

                if (urlTaskId) {
                    // Using unified route with taskId in URL
                    currentTaskId = urlTaskId;
                    console.log('✅ Using taskId from URL:', currentTaskId);
                } else if (storedTaskId) {
                    // Using legacy route with taskId in sessionStorage
                    currentTaskId = storedTaskId;
                    console.log('✅ Using taskId from sessionStorage:', currentTaskId);
                } else {
                    // No taskId found - allow view-only mode but disable save functionality
                    console.warn('⚠️ No taskId found - running in view-only mode');
                    currentTaskId = null;
                }

                setTaskId(currentTaskId);

                // Try to load schedule data using multiple strategies
                let scheduleData: DayColumn[] | null = null;

                if (urlTaskId) {
                    // If we have urlTaskId, try to load from API first (chart from VendorDashboard)
                    console.log('🌐 Loading chart data from API...');
                    try {
                        await loadFromAPI(urlTaskId);
                        scheduleData = await loadScheduleDataSafely();
                    } catch (error) {
                        console.error('❌ Failed to load from API, trying local storage:', error);
                        scheduleData = await loadScheduleDataSafely();
                    }
                } else {
                    // Use local storage strategies (legacy behavior)
                    scheduleData = await loadScheduleDataSafely();
                }

                if (scheduleData) {
                    console.log('📊 [DATA] Raw schedule data:', scheduleData.length, 'days');
                    setScheduleData(scheduleData);

                    // Convert data and store for unprioritized orders
                    const converted = convertWizardToLibraryFormat(scheduleData);
                    console.log('📊 [DATA] Converted data:', {
                        scheduleLength: converted.schedule.length,
                        allOrdersLength: converted.allOrders.length,
                        firstDay: converted.schedule[0]?.date,
                        firstDayDeliveries: converted.schedule[0]?.deliveries?.length || 0
                    });
                    setConvertedData(converted);

                    // Initialize current schedule state
                    setCurrentScheduleState(converted.schedule);
                    console.log('📊 [DATA] Current schedule state initialized with', converted.schedule.length, 'days');
                } else {
                    // Check if we have minimal data and can fetch from API
                    const minimalData = sessionStorage.getItem('dexco-schedule-minimal');
                    if (minimalData && currentTaskId) {
                        console.log('📡 Loading large dataset from API...');
                        await loadFromAPI(currentTaskId);

                        // After API load, try to get the data again
                        const reloadedData = await loadScheduleDataSafely();
                        if (reloadedData) {
                            console.log('📡 [API] Reloaded data after API call:', reloadedData.length, 'days');
                            setScheduleData(reloadedData);
                            const converted = convertWizardToLibraryFormat(reloadedData);
                            setConvertedData(converted);
                            setCurrentScheduleState(converted.schedule);
                            console.log('📡 [API] Current schedule state updated with', converted.schedule.length, 'days');
                        }
                    } else {
                        // No data found, redirect back to main app
                        console.warn('No schedule data found');
                        toast.error('Dados da priorização não encontrados');
                        // Close this tab after 3 seconds
                        setTimeout(() => {
                            if (isUnifiedRoute) {
                                // For unified route, go back to appropriate dashboard
                                navigate(userProfile === 'diretor' ? '/home' : '/dashboard');
                            } else {
                                window.close();
                            }
                        }, 3000);
                    }
                }
            } catch (error) {
                console.error('Error loading schedule data:', error);
                toast.error('Erro ao carregar dados da priorização');
            } finally {
                setIsLoading(false);
            }
        };

        loadScheduleData();
    }, [urlTaskId, isUnifiedRoute, userProfile, navigate]);

    // Safe data loading function with multiple strategies
    const loadScheduleDataSafely = async (): Promise<DayColumn[] | null> => {
        try {
            // Strategy 1: Try direct sessionStorage
            const storedData = sessionStorage.getItem('dexco-schedule-data');
            if (storedData) {
                console.log('✅ Loaded from sessionStorage directly');
                return JSON.parse(storedData);
            }

            // Strategy 2: Try loading from chunks
            const chunksCount = sessionStorage.getItem('dexco-schedule-chunks');
            if (chunksCount) {
                console.log(`📦 Loading from ${chunksCount} chunks...`);
                const scheduleData: DayColumn[] = [];

                for (let i = 0; i < parseInt(chunksCount); i++) {
                    const chunkData = sessionStorage.getItem(`dexco-schedule-chunk-${i}`);
                    if (chunkData) {
                        const chunk: DayColumn[] = JSON.parse(chunkData);
                        scheduleData.push(...chunk);
                    }
                }

                console.log(`✅ Loaded ${scheduleData.length} days from chunks`);
                return scheduleData.length > 0 ? scheduleData : null;
            }

            return null;
        } catch (error) {
            console.error('❌ Error loading schedule data:', error);
            return null;
        }
    };

    // Load data from API when sessionStorage is not available
    const loadFromAPI = async (taskId: string): Promise<void> => {
        try {
            console.log('🌐 Loading chart data from API for UUID:', taskId);

            const response = await getChartByUuid(taskId);

            if (!response.success || !response.data) {
                throw new Error(response.error || 'Erro ao carregar dados do cronograma');
            }

            // Convert CSV to schedule data  
            const scheduleData = parseCsvToDayColumns(response.data);
            console.log('🌐 [API] Parsed CSV data:', scheduleData?.length, 'days');

            if (scheduleData && scheduleData.length > 0) {
                // Store data in sessionStorage for loadScheduleDataSafely to find
                sessionStorage.setItem('dexco-schedule-data', JSON.stringify(scheduleData));
                // Also save the taskId for future save operations
                sessionStorage.setItem('dexco-task-id', taskId);

                console.log(`✅ [API] Data saved with ${scheduleData.length} days`);

                // Log sample day data for debugging
                if (scheduleData[0]) {
                    console.log('🌐 [API] Sample day:', {
                        date: scheduleData[0].date,
                        deliveriesCount: scheduleData[0].deliveries?.length || 0,
                        sampleDelivery: scheduleData[0].deliveries?.[0]
                    });
                }

                toast.success('Cronograma carregado da API com sucesso!');
            } else {
                console.warn('⚠️ [API] No schedule data found');
                toast.error('Nenhum dado de cronograma encontrado');
            }
        } catch (error) {
            console.error('❌ Error loading from API:', error);
            toast.error(`Erro ao carregar cronograma da API: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
            throw error;
        }
    };

    // Function removed - using parseCsvToDayColumns from utils instead

    // Handle save changes - works for all charts with current state
    const handleSaveChanges = async () => {
        console.log('💾 [SAVE] handleSaveChanges called');
        console.log('💾 [SAVE] currentScheduleState:', currentScheduleState?.length, 'days');
        console.log('💾 [SAVE] activeFilters:', activeFilters);

        if (!currentScheduleState) {
            console.error('💾 [SAVE] currentScheduleState is null/undefined');
            toast.error('Estado atual do cronograma não encontrado.');
            return;
        }

        if (!taskId) {
            toast.error('ID da tarefa não encontrado. Não é possível salvar as alterações.');
            return;
        }

        // Validate that taskId is a valid UUID (required by backend)
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (!uuidRegex.test(taskId)) {
            toast.error('ID da tarefa inválido. Deve ser um UUID válido para salvar as alterações.');
            return;
        }

        setIsUploadingCsv(true);
        try {
            // For save operations, send the complete schedule (not filtered)
            // The filtering should be done for visual purposes only
            const csvContent = generateCsvFromScheduleState(currentScheduleState);

            // Create a file with the CSV content
            const csvFile = new File([csvContent], 'schedule-update.csv', { type: 'text/csv' });

            // Create FormData to send the file
            const formData = new FormData();
            formData.append('file', csvFile);

            // Send to the update_optimizer_results endpoint
            const API_BASE = process.env.NODE_ENV === 'development'
                ? 'http://localhost:8000'
                : 'https://dexcobackend-v1-production.up.railway.app';

            const response = await fetch(`${API_BASE}/api/update_optimizer_results/`, {
                method: 'POST',
                headers: {
                    ...getAuthHeader()
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.text();

            // Allow multiple saves and exports - no need to set confirmation state
            toast.success('Alterações salvas com sucesso!');
            console.log('✅ Save result:', result);

        } catch (error) {
            console.error('Error saving changes:', error);
            toast.error('Erro ao salvar alterações');
        } finally {
            setIsUploadingCsv(false);
        }
    };

    // Generate CSV content from current schedule state
    const generateCsvFromScheduleState = (schedule: LibraryDayColumn[]): string => {
        console.log('📋 [CSV] Starting CSV generation...');
        console.log('📋 [CSV] taskId:', taskId);
        console.log('📋 [CSV] schedule length:', schedule?.length);
        console.log('📋 [CSV] activeFilters:', activeFilters);

        if (!taskId) {
            throw new Error('taskId is required to generate CSV for optimizer results');
        }

        // Validate that taskId is a valid UUID
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (!uuidRegex.test(taskId)) {
            throw new Error('taskId must be a valid UUID for optimizer results');
        }

        if (!schedule || schedule.length === 0) {
            console.warn('📋 [CSV] Empty or null schedule provided');
            return '';
        }

        // CSV headers matching the expected format for update_optimizer_results (20 columns)
        const csvHeaders = [
            'id',
            'ordem',
            'item',
            'email_vendedor',
            'centro',
            'cli_cod_pai',
            'cli_razao_social',
            'no_vendedor',
            'cd_produto',
            'prd_dc_linha',
            'prd_espessura',
            'prd_largura',
            'prd_comprimento',
            'prd_tx_breve',
            'qt_vol_m3_liquido_gerencial',
            'dt_emissao',
            'dt_preparacao_material',
            'dt_producao',
            'request_uuid',
            'prd_confirmed'
        ];

        // Convert schedule data to CSV rows
        const csvRows = [csvHeaders.join(',')];
        let totalDeliveries = 0;
        let filteredDeliveries = 0;

        schedule.forEach((day, dayIndex) => {
            console.log(`📋 [CSV] Day ${dayIndex}: ${day.date}, deliveries: ${day.deliveries?.length || 0}`);

            if (!day.deliveries || day.deliveries.length === 0) {
                console.log(`📋 [CSV] Day ${dayIndex} has no deliveries`);
                return;
            }

            day.deliveries.forEach((delivery, deliveryIndex) => {
                totalDeliveries++;

                // Apply vendor filter if active (only include records for the active vendor)
                if (activeFilters.vendedor && delivery.no_vendedor !== activeFilters.vendedor) {
                    console.log(`📋 [CSV] Filtering out delivery ${deliveryIndex}: ${delivery.no_vendedor} != ${activeFilters.vendedor}`);
                    return; // Skip this delivery if it doesn't match the active vendor filter
                }

                filteredDeliveries++;

                // Detect split cards (IDs with format "originalId-number") and send empty ID
                // This allows the backend to generate a new ID for split cards
                const isSplitCard = delivery.id && delivery.id.includes('-') && /\d+-\d+/.test(delivery.id);
                const csvId = isSplitCard ? '' : (delivery.id || '');

                if (isSplitCard) {
                    console.log(`📋 [CSV] Split card detected: ${delivery.id} -> sending empty ID to backend`);
                }
                console.log(`📋 [CSV] Including delivery ${deliveryIndex}: ${delivery.no_vendedor}, id: ${delivery.id} (CSV id: ${csvId})`);

                const row = [
                    csvId,
                    delivery.ordem || '',
                    delivery.item || '',
                    delivery.email_vendedor || 'Não informado',
                    delivery.centro || '',
                    (delivery.cli_cod_pai || 0).toString(),
                    `"${delivery.cli_razao_social || ''}"`,
                    `"${delivery.no_vendedor || 'Não informado'}"`, // Fallback for empty no_vendedor
                    delivery.cd_produto || '',
                    `"${delivery.prd_dc_linha || ''}"`,
                    (delivery.espessura || delivery.prd_espessura || 0).toString(),
                    (delivery.largura || parseFloat(delivery.prd_largura || '0') || 0).toString(),
                    (delivery.comprimento || parseFloat(delivery.prd_comprimento || '0') || 0).toString(),
                    `"${delivery.produto || delivery.prd_tx_breve || `P MP-SUPIN ${delivery.prd_espessura || 15}.0 1850 2750 00044`}"`,
                    (delivery.volume || delivery.units || 0).toFixed(2),
                    delivery.dt_emissao || '',
                    delivery.dt_preparacao_material || '',
                    day.date, // Production date from day (this is what was modified by the user)
                    taskId, // Use taskId as request_uuid (required)
                    (delivery.prd_confirmed || false).toString()
                ];
                csvRows.push(row.join(','));
            });
        });

        console.log(`📋 [CSV] Total deliveries: ${totalDeliveries}, Filtered deliveries: ${filteredDeliveries}`);
        console.log(`📋 [CSV] Final CSV rows: ${csvRows.length} (including header)`);

        const result = csvRows.join('\n');
        console.log('📋 [CSV] Generated CSV preview (first 500 chars):', result.substring(0, 500));

        return result;
    };





    // Handle export/download using current schedule state
    const handleDownload = () => {
        console.log('⬇️ [EXPORT] handleDownload called');
        console.log('⬇️ [EXPORT] currentScheduleState:', currentScheduleState?.length, 'days');
        console.log('⬇️ [EXPORT] activeFilters:', activeFilters);

        if (!currentScheduleState) {
            console.error('⬇️ [EXPORT] currentScheduleState is null/undefined');
            toast.error('Estado atual do cronograma não encontrado');
            return;
        }

        try {
            // Generate CSV from complete schedule (not just visible week)
            const csvContent = generateCsvFromScheduleState(currentScheduleState);

            // Create and download the file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `cronograma_producao_${taskId || 'export'}_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            toast.success('CSV exportado com sucesso');
        } catch (error) {
            console.error('Erro ao exportar CSV:', error);
            toast.error('Erro ao exportar CSV');
        }
    };

    // Handle unprioritized orders
    const handleUnprioritizedOrders = async () => {
        if (!convertedData) {
            toast.error('Dados do cronograma não carregados');
            return;
        }

        setIsLoadingUnprioritizedModal(true);

        try {
            // Simulate a small delay to show loading state
            await new Promise(resolve => setTimeout(resolve, 300));
            setIsUnprioritizedModalOpen(true);
        } catch (error) {
            console.error('Erro ao abrir modal de pedidos não priorizados:', error);
            toast.error('Erro ao carregar pedidos não priorizados');
        } finally {
            setIsLoadingUnprioritizedModal(false);
        }
    };

    // Handle save prioritized orders
    const handleSavePrioritizedOrders = async (prioritizedOrders: LibraryDeliveryCard[]) => {
        if (!convertedData) return;

        // Create new schedule with prioritized orders added
        const newSchedule = [...convertedData.schedule];

        // Add prioritized orders to their respective dates
        prioritizedOrders.forEach(order => {
            if (order.dt_producao) {
                const targetDay = newSchedule.find(day => day.date === order.dt_producao);
                if (targetDay) {
                    targetDay.deliveries.push(order);
                } else {
                    // Create new day if it doesn't exist
                    newSchedule.push({
                        date: order.dt_producao,
                        deliveries: [order]
                    });
                }
            }
        });

        // Sort days by date
        newSchedule.sort((a, b) => a.date.localeCompare(b.date));

        // Remove prioritized orders from allOrders
        const updatedAllOrders = convertedData.allOrders.filter(order =>
            !prioritizedOrders.find(p => p.id === order.id)
        );

        // Update both converted data and current schedule state
        const updatedConvertedData = {
            schedule: newSchedule,
            allOrders: updatedAllOrders
        };
        setConvertedData(updatedConvertedData);
        setCurrentScheduleState(newSchedule); // Keep current state in sync

        // No need to update sessionStorage here - it's only updated on save

        toast.success(`${prioritizedOrders.length} pedidos priorizados com sucesso!`);
    };

    // Handle share
    const handleShare = async () => {
        try {
            if (navigator.share) {
                await navigator.share({
                    title: 'Priorização de Vendas - Dexco',
                    text: 'Confira a priorização de vendas gerada pelo sistema Dexco',
                    url: window.location.href,
                });
                toast.success('Priorização compartilhada');
            } else {
                // Fallback: copy to clipboard
                await navigator.clipboard.writeText(window.location.href);
                toast.success('Link copiado para área de transferência');
            }
        } catch (error) {
            console.error('Error sharing:', error);
            toast.error('Erro ao compartilhar priorização');
        }
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="min-h-screen bg-dexco-gray-bg flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-dexco-green-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-dexco-text-dark font-body">Carregando cronograma...</p>
                </div>
            </div>
        );
    }

    // No data state
    if (!scheduleData) {
        return (
            <div className="min-h-screen bg-dexco-gray-bg flex items-center justify-center">
                <div className="text-center p-8">
                    <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <ChevronLeft className="w-12 h-12 text-red-500" />
                    </div>
                    <h1 className="text-2xl font-display font-semibold text-dexco-text-dark mb-4">
                        Dados não encontrados
                    </h1>
                    <p className="text-gray-600 font-body mb-6">
                        Não foi possível carregar os dados do cronograma.
                    </p>
                    <SecondaryButton
                        onClick={() => window.close()}
                        leftIcon={<ChevronLeft className="w-4 h-4" />}
                    >
                        Fechar Janela
                    </SecondaryButton>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-dexco-gray-bg">
            <div className="flex flex-col">
                {/* Header */}
                <div className="border-b border-dexco-border bg-white/95 backdrop-blur-sm">
                    {/* Header: Title and Action buttons in same line */}
                    <div className="px-4 sm:px-6 py-4">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            {/* Left: Back button, Logo and Title */}
                            <div className="flex items-center gap-4 min-w-0 flex-1">
                                {shouldShowBackButton && (
                                    <SecondaryButton
                                        onClick={onBack || (() => {
                                            if (returnTo) {
                                                window.location.href = returnTo;
                                            } else if (isUnifiedRoute) {
                                                // For unified route, navigate to appropriate dashboard
                                                navigate(userProfile === 'diretor' ? '/home' : '/dashboard');
                                            } else {
                                                window.history.back();
                                            }
                                        })}
                                        leftIcon={<ChevronLeft className="w-4 h-4" />}
                                        className="flex-shrink-0"
                                    >
                                        Voltar
                                    </SecondaryButton>
                                )}
                                <img
                                    src="/dexco-logo.svg"
                                    alt="Dexco"
                                    className="h-8 w-auto flex-shrink-0"
                                    onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                    }}
                                />
                                <div className="min-w-0 flex-1">
                                    <h1 className="text-lg sm:text-xl font-display font-semibold text-dexco-text-dark truncate">
                                        Priorização de Vendas
                                    </h1>
                                    {currentUserInfo && (
                                        <div className="hidden sm:block mt-1">
                                            <p className="text-xs text-gray-500 truncate">
                                                {currentUserInfo.full_name} • {currentUserInfo.email} • {currentUserInfo.profile_type === 'diretor' ? 'Diretor' : 'Vendedor'}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Right: Action buttons */}
                            <div className="flex-shrink-0 w-full sm:w-auto">
                                {/* Mobile: Stack buttons vertically or horizontal scroll */}
                                <div className="flex sm:hidden flex-col gap-2">
                                    <SecondaryButton
                                        onClick={handleUnprioritizedOrders}
                                        leftIcon={isLoadingUnprioritizedModal ?
                                            <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" /> :
                                            <Calendar className="w-4 h-4" />
                                        }
                                        className="flex-shrink-0 justify-center"
                                        disabled={isLoadingUnprioritizedModal}
                                    >
                                        {isLoadingUnprioritizedModal ? 'Carregando...' : 'Pedidos não priorizados'}
                                        {!isLoadingUnprioritizedModal && unprioritizedCount > 0 && (
                                            <span className="ml-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full min-w-[20px] flex items-center justify-center">
                                                {unprioritizedCount}
                                            </span>
                                        )}
                                    </SecondaryButton>

                                    <SecondaryButton
                                        onClick={handleShare}
                                        leftIcon={<Share2 className="w-4 h-4" />}
                                        className="flex-shrink-0 justify-center"
                                    >
                                        Compartilhar
                                    </SecondaryButton>

                                    {taskId && (
                                        <PrimaryButton
                                            onClick={handleSaveChanges}
                                            leftIcon={isUploadingCsv ? undefined : <CheckCircle className="w-4 h-4" />}
                                            className="flex-shrink-0 justify-center w-full"
                                            disabled={isUploadingCsv}
                                        >
                                            {isUploadingCsv ? 'Salvando...' : 'Salvar Alterações'}
                                        </PrimaryButton>
                                    )}

                                    {!taskId && (
                                        <div className="flex-shrink-0 justify-center w-full p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                                            <p className="text-sm text-yellow-700 text-center">
                                                📋 Modo somente leitura - Para salvar alterações, acesse via otimização ativa
                                            </p>
                                        </div>
                                    )}

                                    <SecondaryButton
                                        onClick={handleDownload}
                                        leftIcon={<Download className="w-4 h-4" />}
                                        className="flex-shrink-0 justify-center"
                                    >
                                        Exportar
                                    </SecondaryButton>
                                </div>

                                {/* Desktop: Show all buttons in line with flex-wrap */}
                                <div className="hidden sm:flex items-center gap-x-2 flex-wrap">
                                    <SecondaryButton
                                        onClick={handleUnprioritizedOrders}
                                        leftIcon={isLoadingUnprioritizedModal ?
                                            <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" /> :
                                            <Calendar className="w-4 h-4" />
                                        }
                                        className="flex-shrink-0"
                                        disabled={isLoadingUnprioritizedModal}
                                    >
                                        {isLoadingUnprioritizedModal ? 'Carregando...' : 'Pedidos não priorizados'}
                                        {!isLoadingUnprioritizedModal && unprioritizedCount > 0 && (
                                            <span className="ml-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full min-w-[20px] flex items-center justify-center">
                                                {unprioritizedCount}
                                            </span>
                                        )}
                                    </SecondaryButton>

                                    <SecondaryButton
                                        onClick={handleShare}
                                        leftIcon={<Share2 className="w-4 h-4" />}
                                        className="flex-shrink-0"
                                    >
                                        Compartilhar
                                    </SecondaryButton>

                                    {taskId && (
                                        <PrimaryButton
                                            onClick={handleSaveChanges}
                                            leftIcon={isUploadingCsv ? undefined : <CheckCircle className="w-4 h-4" />}
                                            className="flex-shrink-0"
                                            disabled={isUploadingCsv}
                                        >
                                            {isUploadingCsv ? 'Salvando...' : 'Salvar Alterações'}
                                        </PrimaryButton>
                                    )}

                                    {!taskId && (
                                        <div className="flex-shrink-0 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                                            <p className="text-sm text-yellow-700 text-center">
                                                📋 Modo somente leitura - Para salvar alterações, acesse via otimização ativa
                                            </p>
                                        </div>
                                    )}

                                    <SecondaryButton
                                        onClick={handleDownload}
                                        leftIcon={<Download className="w-4 h-4" />}
                                        className="flex-shrink-0"
                                    >
                                        Exportar
                                    </SecondaryButton>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Chart Container */}
                <div className="flex-1 px-4 sm:px-6 py-6 pb-16 w-full max-w-full"> {/* Increased pb-8 to pb-16 for more breathing room */}
                    <div className="w-full max-w-full">
                        {convertedData && (
                            <SalesStackedSchedule
                                month={convertedData.schedule}
                                monthName={getMonthNameFromSchedule(scheduleData || [])}
                                onScheduleChange={(newSchedule: LibraryDayColumn[]) => {
                                    // Update current schedule state when user modifies the chart
                                    console.log('📊 Schedule updated by user interaction');
                                    setCurrentScheduleState(newSchedule);

                                    // Don't update convertedData here to avoid loops
                                    // Only currentScheduleState is needed for saving/exporting
                                }}
                                onFiltersChange={(filters: { vendedor: string; cliente: string; espessura: string; largura: string }) => {
                                    setActiveFilters(filters);
                                }}
                                initialWeek={0}
                                userInfo={currentUserInfo}
                                taskId={taskId}
                                onShowToast={(message: string, type: 'success' | 'error' | 'warning') => {
                                    if (type === 'error') {
                                        toast.error(message);
                                    } else if (type === 'warning') {
                                        toast(message, { icon: '⚠' });
                                    } else {
                                        toast.success(message);
                                    }
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>



            {/* Unprioritized Orders Modal */}
            <UnprioritizedOrdersModal
                isOpen={isUnprioritizedModalOpen}
                onClose={() => setIsUnprioritizedModalOpen(false)}
                onSave={handleSavePrioritizedOrders}
                allOrders={convertedData?.allOrders || []}
                isLoading={isLoadingUnprioritizedModal || isLoading}
                userInfo={currentUserInfo}
                getQuotaForDate={(_date: string) => {
                    // Implementar busca de cota se necessário
                    // Por enquanto retornamos null (sem limite)
                    return null;
                }}
                monthState={currentScheduleState || []}
            />
        </div>
    );
}; 